import { statusLabel, statusColor } from '../status-presentation'
import { AvailabilityBadge } from '../availability-presentation'
import '../ig-story.css'
import ReportFilters, { StatusOptions, CountOptions } from '../report-filters'
import { getIgStorySaDisplayName } from '../sa-display-name'
import QueuePagination from '../queue-pagination'
import AuditLogRow from '../audit-log-row'
import SalesIgStoryApi from 'api/admins/sales-ig-story-api'
import { TitlePage } from 'components/atoms'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
  Table
} from 'reactstrap'

const propTypes = {
  auditMode: PropTypes.bool,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string
  }),
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const STATUS = {
  IN_QUEUE : 'in_queue',
  REPLACED : 'replaced'
}

const GROUP_OPTIONS = ['', 'BKA', 'BKR', 'BKV', 'BRV']
const CATEGORY_OPTIONS = ['', 'C', 'H']
const DEFAULT_PER_PAGE = 100
const emptyProgressFilters = { assignee_id: '', internal_category: '', status: '', arrival_from: '', arrival_to: '' }
const invalidDateRange = (from, to) => Boolean(from && to && from > to)
const selectionStorageKey = (adminId) => `ig-story-my-queue-selected-${adminId}`

const readStoredSelection = (adminId) => {
  try {
    const ids = JSON.parse(window.sessionStorage.getItem(selectionStorageKey(adminId)) || '[]')
    return Array.isArray(ids) ? [...new Set(ids.filter((id) => Number.isSafeInteger(id) && id > 0))] : []
  }
  catch (_error) { return [] }
}

const readPageNumber = (value) => {
  const page = Number(value)
  return Number.isSafeInteger(page) && page > 0 ? page : 1
}

const emptyMeta = {
  current_page : 1,
  per_page     : DEFAULT_PER_PAGE,
  total_count  : 0,
  total_pages  : 1,
  assignees    : [],
  product_categories: []
}

const emptyViewer = {
  admin             : null,
  member_role       : null,
  internal_category : null,
  capacity          : null,
  can_audit         : false,
  can_view_audit_logs : false,
  can_manage        : false,
  can_edit_assignments : false,
  counts            : {
    in_queue : 0,
    done     : 0,
    replaced : 0
  }
}

// Formats API timestamps in Jakarta-friendly browser output while preserving a clear fallback.
const formatDateTime = (value) => {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'

  return new Intl.DateTimeFormat('id-ID', {
    dateStyle : 'medium',
    timeStyle : 'short',
    timeZone  : 'Asia/Jakarta'
  }).format(date)
}

// Normalizes backend status values into labels familiar to non-technical Sales users.


// Extracts server-provided filenames when CSV responses include Content-Disposition.
const readFilename = (response, fallback) => {
  const disposition = response?.headers?.['content-disposition']
  const match = disposition?.match(/filename="?([^";]+)"?/i)
  return match?.[1] || fallback
}

// Triggers a browser download for CSV blobs returned by the Rails API.
const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

// Main Sales Team page for the rolling IG Story assignment queue.
const SalesIgStoryPage = ({ history, location, pageUtils, auditMode = false }) => {
  // My Queue uses the URL as its source of truth, including after returning from Detail.
  const myQueueParams = new URLSearchParams(auditMode ? '' : (location?.search || history.location.search || ''))
  const updateMyQueueQuery = useCallback((changes, replace = false) => {
    const params = new URLSearchParams(history.location.search || '')
    Object.entries(changes).forEach(([key, value]) => {
      if (value === '' || value === undefined || value === null) params.delete(key)
      else params.set(key, String(value))
    })
    const search = params.toString()
    history[replace ? 'replace' : 'push']({
      pathname: history.location.pathname,
      search: search ? `?${search}` : ''
    })
  }, [history])
  const queueRequest = useRef(0)
  const progressRequest = useRef(0)
  const [progressFilters, setProgressFilters] = useState(emptyProgressFilters)
  const [progressLoading, setProgressLoading] = useState(false)
  const [exportingProgress, setExportingProgress] = useState(false)
  const [arrivalFrom, setArrivalFrom] = useState('')
  const [arrivalTo, setArrivalTo] = useState('')
  const [countFilter, setCountFilter] = useState('')
  const queueRangeInvalid = invalidDateRange(arrivalFrom, arrivalTo)
  const progressRangeInvalid = invalidDateRange(progressFilters.arrival_from, progressFilters.arrival_to)
  const [ status, setStatus ] = useState('')
  const [ progress, setProgress ] = useState([])
  const [ auditPage, setAuditPage ] = useState(1)
  const [ auditMeta, setAuditMeta ] = useState(emptyMeta)
  const [ queue, setQueue ] = useState([])
  const [ meta, setMeta ] = useState(emptyMeta)
  const [ selectedQueueIds, setSelectedQueueIds ] = useState([])
  const selectionOwner = useRef(null)
  const [ savingBulk, setSavingBulk ] = useState(false)
  const [ viewer, setViewer ] = useState(emptyViewer)
  const [ auditQueuePage, setAuditQueuePage ] = useState(1)
  const [ auditQ, setAuditQ ] = useState('')
  const [ searchInput, setSearchInput ] = useState(() => auditMode ? '' : (myQueueParams.get('q') || ''))
  const [ auditGroupCode, setAuditGroupCode ] = useState('')
  const [ auditCategory, setAuditCategory ] = useState('')
  const [ auditAssigneeId, setAuditAssigneeId ] = useState('')
  const queuePage = auditMode ? auditQueuePage : readPageNumber(myQueueParams.get('page'))
  const q = auditMode ? auditQ : (myQueueParams.get('q') || '')
  const groupCode = auditMode ? auditGroupCode : (myQueueParams.get('group') || '')
  const category = auditMode ? auditCategory : (myQueueParams.get('category') || '')
  const productCategoryId = auditMode ? '' : (myQueueParams.get('product_category_id') || '')
  const assigneeId = auditMode ? auditAssigneeId : (myQueueParams.get('sa') || '')
  // This filter tracks completed upload cycles, not unsaved checkbox selection.
  const uploadHistoryFilter = ['ever', 'never'].includes(myQueueParams.get('upload_history'))
    ? myQueueParams.get('upload_history') : ''
  const setQueuePage = useCallback((nextPage, replace = false) => {
    if (auditMode) return setAuditQueuePage(nextPage)
    const page = typeof nextPage === 'function' ? nextPage(queuePage) : nextPage
    updateMyQueueQuery({ page: page > 1 ? page : '' }, replace)
  }, [auditMode, queuePage, updateMyQueueQuery])
  const setQ = (value) => auditMode ? setAuditQ(value) : updateMyQueueQuery({ q: value, page: '' })
  const setGroupCode = (value) => auditMode ? setAuditGroupCode(value) : updateMyQueueQuery({ group: value, page: '' })
  const setCategory = (value) => auditMode ? setAuditCategory(value) : updateMyQueueQuery({ category: value, page: '' })
  const setProductCategoryId = (value) => updateMyQueueQuery({ product_category_id: value, page: '' })
  const setAssigneeId = (value) => auditMode ? setAuditAssigneeId(value) : updateMyQueueQuery({ sa: value, page: '' })
  const [ fromDate, setFromDate ] = useState('')
  const [ toDate, setToDate ] = useState('')
  const [ loadingQueue, setLoadingQueue ] = useState(true)
  const [ exporting, setExporting ] = useState(false)
  const [ message, setMessage ] = useState('')
  const [ errorMessage, setErrorMessage ] = useState('')
  const [ auditOpen, setAuditOpen ] = useState(false)
  const [ setupOpen, setSetupOpen ] = useState(false)
  const [ auditLogs, setAuditLogs ] = useState([])
  const [ auditLoading, setAuditLoading ] = useState(false)
  const [ members, setMembers ] = useState([])
  const [ administrators, setAdministrators ] = useState([])
  const [ setupLoading, setSetupLoading ] = useState(false)
  const [ memberForm, setMemberForm ] = useState({
    administrator_id  : '',
    member_role       : 'assignee',
    internal_category : 'C',
    capacity          : 100,
    sort_order        : 0,
    active            : true
  })

  // Builds one filter payload shared by the queue and its export.
  const baseFilters = useCallback(() => ({
    q: q || undefined,
    group_code: groupCode || undefined,
    internal_category: category || undefined,
    product_category_id: productCategoryId || undefined,
    assignee_id: assigneeId || undefined,
    arrival_from: arrivalFrom || undefined,
    arrival_to: arrivalTo || undefined,
    count_filter: countFilter || undefined,
    per_page: auditMode ? 15 : DEFAULT_PER_PAGE
  }), [q, groupCode, category, productCategoryId, assigneeId, arrivalFrom, arrivalTo, countFilter, auditMode])

  // My Queue loads only unfinished assignments; Audit Queue applies its selected status.
  const loadQueue = useCallback(async () => {
    const requestId = ++queueRequest.current
    setLoadingQueue(true)
    setErrorMessage('')
    if (queueRangeInvalid) { setQueue([]); setLoadingQueue(false); return }

    try {
      const response = await (auditMode ? SalesIgStoryApi.auditQueue : SalesIgStoryApi.index)({
        params: {
          ...baseFilters(),
          status: auditMode ? status : STATUS.IN_QUEUE,
          page: queuePage,
          per_page: 15,
          ...(!auditMode && uploadHistoryFilter ? { upload_history: uploadHistoryFilter } : {})
        }
      })
      if (requestId !== queueRequest.current) return
      // Restore any unsaved checkbox selections independently of completed-upload history.
      const adminId = response.data.viewer?.admin?.id
      if (!auditMode && adminId && selectionOwner.current !== adminId) {
        selectionOwner.current = adminId
        const restoredIds = readStoredSelection(adminId)
        setSelectedQueueIds(restoredIds)
      }
      const responseMeta = response.data.meta || emptyMeta
      if (queuePage > responseMeta.total_pages) {
        setQueuePage(Math.max(responseMeta.total_pages, 1), true)
        return
      }
      setQueue(response.data.assignments || [])
      setMeta(responseMeta)
      setViewer(response.data.viewer || emptyViewer)
    }
    catch (error) {
      if (requestId === queueRequest.current) {
        setQueue([])
        setErrorMessage(error?.response?.data?.message || 'Gagal memuat Queue IG Story.')
      }
    }
    finally {
      if (requestId === queueRequest.current) setLoadingQueue(false)
    }
  }, [baseFilters, queuePage, auditMode, status, queueRangeInvalid, uploadHistoryFilter, setQueuePage])

  const loadProgress = useCallback(async () => {
    if (!auditMode) return
    const requestId = ++progressRequest.current
    if (progressRangeInvalid) { setProgress([]); setProgressLoading(false); return }
    setProgressLoading(true)
    try {
      const response = await SalesIgStoryApi.progress({ params: progressFilters })
      if (requestId === progressRequest.current) setProgress(response.data.progress || [])
    }
    catch (error) {
      if (requestId === progressRequest.current) {
        setProgress([])
        setErrorMessage(error?.response?.data?.message || 'Gagal memuat progress SA.')
      }
    }
    finally { if (requestId === progressRequest.current) setProgressLoading(false) }
  }, [auditMode, progressFilters, progressRangeInvalid])

  useEffect(() => { loadProgress() }, [loadProgress])

  // Load the active queue when the page or its filters change.
  useEffect(() => {
    loadQueue()
  }, [loadQueue])

  // Keep search text aligned when navigating with browser Back / Forward.
  useEffect(() => {
    if (!auditMode) setSearchInput(q)
  }, [auditMode, q])

  // Audit Queue retains its existing local filter/pagination behavior.
  useEffect(() => {
    if (auditMode) setAuditQueuePage(1)
  }, [auditMode, q, groupCode, category, productCategoryId, assigneeId, arrivalFrom, arrivalTo, countFilter, status])

  // Normal SAs are restricted to their configured category; keep the current page intact.
  useEffect(() => {
    if (!auditMode && !viewer.can_audit && viewer.internal_category && !category) {
      updateMyQueueQuery({ category: viewer.internal_category }, true)
    }
  }, [auditMode, viewer.can_audit, viewer.internal_category, category, updateMyQueueQuery])

  // Selection is temporary until Save Selected succeeds, but survives page and Detail navigation.
  useEffect(() => {
    const adminId = viewer.admin?.id
    if (auditMode || !adminId || selectionOwner.current !== adminId) return
    try {
      if (selectedQueueIds.length) {
        window.sessionStorage.setItem(selectionStorageKey(adminId), JSON.stringify(selectedQueueIds))
      }
      else window.sessionStorage.removeItem(selectionStorageKey(adminId))
    }
    catch (_error) { /* Storage may be unavailable in private browsing. */ }
  }, [auditMode, viewer.admin?.id, selectedQueueIds])

  // Reloads setup data only when managers explicitly open the configuration section.
  const loadMembers = useCallback(async () => {
    if (!viewer.can_manage) return

    setSetupLoading(true)
    try {
      const response = await SalesIgStoryApi.members()
      setMembers(response.data.members || [])
      setAdministrators((response.data.administrators || []).filter(
        (admin) => String(admin.role || '').trim().toLowerCase() === 'sales'
      ))
    }
    catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Gagal memuat konfigurasi IG Story.')
    }
    finally {
      setSetupLoading(false)
    }
  }, [viewer.can_manage])

  // Fetches audit rows on demand so normal SA page loads do not query legacy AuditLogEntry data.
  const loadAuditLogs = useCallback(async () => {
    if (!viewer.can_view_audit_logs) return

    setAuditLoading(true)
    try {
      const response = await SalesIgStoryApi.auditLogs({
        params: { from_date: fromDate || undefined, to_date: toDate || undefined, per_page: 25, page: auditPage }
      })
      setAuditLogs(response.data.audit_logs || [])
      setAuditMeta(response.data.meta || emptyMeta)
    }
    catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Gagal memuat Audit Log IG Story.')
    }
    finally {
      setAuditLoading(false)
    }
  }, [viewer.can_view_audit_logs, fromDate, toDate, auditPage])

  useEffect(() => { if (auditOpen) loadAuditLogs() }, [auditOpen, loadAuditLogs])
  useEffect(() => { setAuditPage(1) }, [fromDate, toDate])

  // Opens an assignment cycle; backend still owns visibility and action authorization.
  const openAssignmentDetail = (assignmentId) => {
    const returnTo = `${history.location.pathname}${history.location.search || ''}`
    history.push(`/app/sales/ig-story/${assignmentId}?return_to=${encodeURIComponent(returnTo)}`)
  }

  const openSaAssignmentDetail = (administratorId) => {
    history.push(`/app/sales/ig-story/sa/${administratorId}/assignments`)
  }

  const selectedQueueIdSet = useMemo(() => new Set(selectedQueueIds), [selectedQueueIds])
  const allCurrentPageSelected = !auditMode && queue.length > 0 &&
    queue.every((assignment) => selectedQueueIdSet.has(assignment.id))

  const toggleQueueAssignment = (assignmentId) => {
    setSelectedQueueIds((current) => (
      current.includes(assignmentId)
        ? current.filter((id) => id !== assignmentId)
        : [ ...current, assignmentId ]
    ))
  }

  // Select All affects only visible rows, without clearing selections on other pages.
  const toggleCurrentQueuePage = () => {
    const pageIds = queue.map((assignment) => assignment.id)
    setSelectedQueueIds((current) => allCurrentPageSelected
      ? current.filter((id) => !pageIds.includes(id))
      : [...new Set([...current, ...pageIds])]
    )
  }

  const saveSelectedQueue = async () => {
    if (auditMode || savingBulk || selectedQueueIds.length === 0) return

    setSavingBulk(true)
    setErrorMessage('')

    try {
      const response = await SalesIgStoryApi.markDone({ assignmentIds: selectedQueueIds })
      setSelectedQueueIds([])
      setMessage(response?.data?.message || `${selectedQueueIds.length} upload(s) confirmed.`)

      // Preserve the SA's current page; loadQueue only clamps it if it no longer exists.
      await loadQueue()
    }
    catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Gagal menyimpan bulk upload IG Story.')
    }
    finally {
      setSavingBulk(false)
    }
  }

  // Each report exports its own filters, without table pagination.
  const exportReport = async (report) => {
    const isProgress = report === 'progress'
    if (isProgress ? exportingProgress || progressRangeInvalid : exporting || queueRangeInvalid) return
    const setBusy = isProgress ? setExportingProgress : setExporting
    setBusy(true)
    try {
      const filters = isProgress ? progressFilters : { ...baseFilters(), status }
      const { per_page, page, ...exportFilters } = filters
      const response = await SalesIgStoryApi.exportXlsx({ params: { ...exportFilters, report } })
      downloadBlob(response.data, readFilename(response, `ig_story_${report}_${Date.now()}.xlsx`))
    }
    catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Gagal export XLSX IG Story.')
    }
    finally { setBusy(false) }
  }

  // Adds one data-driven assignee/auditor configuration row.
  const createMember = async () => {
    if (!memberForm.administrator_id) {
      setErrorMessage('Pilih administrator terlebih dahulu.')
      return
    }

    setSetupLoading(true)
    try {
      await SalesIgStoryApi.createMember({ payload: memberForm })
      setMemberForm({
        administrator_id  : '',
        member_role       : 'assignee',
        internal_category : 'C',
        capacity          : 100,
        sort_order        : 0,
        active            : true
      })
      await loadMembers()
      await loadQueue()
    }
    catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Gagal menambahkan member IG Story.')
    }
    finally {
      setSetupLoading(false)
    }
  }

  // Removes feature membership only; backend keeps assignment history and blocks destructive side effects.
  const deleteMember = async (id) => {
    setSetupLoading(true)
    try {
      await SalesIgStoryApi.deleteMember({ id })
      await loadMembers()
      await loadQueue()
    }
    catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Gagal menghapus member IG Story.')
    }
    finally {
      setSetupLoading(false)
    }
  }

  // Opens setup and lazy-loads member data on first interaction.
  const toggleSetup = () => {
    const nextOpen = !setupOpen
    setSetupOpen(nextOpen)
    if (nextOpen) loadMembers()
  }

  // Opens audit and lazy-loads logs on first interaction.
  const toggleAudit = () => {
    const nextOpen = !auditOpen
    setAuditOpen(nextOpen)

  }

  // Derives the visual quota value for SA mode; manager mode intentionally avoids a misleading single capacity.
  const quotaLabel = useMemo(() => {
    if (viewer.capacity) return `${viewer.counts.in_queue || 0} / ${viewer.capacity}`
    return `${viewer.counts.in_queue || 0}`
  }, [viewer.capacity, viewer.counts.in_queue])

  // Continue numbering across the 15-row pages in both queue views.
  const queueRowStart = (Math.max(Number(meta.current_page || queuePage), 1) - 1) *
    Math.max(Number(meta.per_page || 15), 1)

  return (
    <Container fluid className='ig-story-page'>
      <TitlePage mainTitle='IG Story' subTitle={ auditMode ? 'Audit Queue' : 'My Queue' } />

      <Row className='mb-3'>
        <Col lg='4' md='6' className='mb-3'>
          <Card className='h-100'>
            <CardBody>
              <small className='text-muted'>Logged in as</small>
              <h4 className='mb-1'>{ viewer.admin?.name || '-' }</h4>
              <div className='text-muted'>{ viewer.admin?.email || '-' }</div>
            </CardBody>
          </Card>
        </Col>
        <Col lg='2' md='6' className='mb-3'>
          <Card className='h-100'>
            <CardBody>
              <small className='text-muted'>Category</small>
              <h3 className='mb-0'>{ viewer.internal_category || (viewer.can_audit ? 'All' : '-') }</h3>
            </CardBody>
          </Card>
        </Col>
        <Col lg='2' md='4' className='mb-3'>
          <Card className='h-100'>
            <CardBody>
              <small className='text-muted'>Pending Uploads / Quota</small>
              <h3 className='mb-0'>{ quotaLabel }</h3>
            </CardBody>
          </Card>
        </Col>
        { auditMode && (
          <Col lg='2' md='4' className='mb-3'>
            <Card className='h-100'>
              <CardBody>
                <small className='text-muted'>Completed Uploads</small>
                <h3 className='mb-0 text-success'>{ viewer.counts.done || 0 }</h3>
              </CardBody>
            </Card>
          </Col>
        ) }
        <Col lg='2' md='4' className='mb-3'>
          <Card className='h-100'>
            <CardBody>
              <small className='text-muted'>Replaced</small>
              <h3 className='mb-0 text-danger'>{ viewer.counts.replaced || 0 }</h3>
            </CardBody>
          </Card>
        </Col>
      </Row>

      { message && <Alert color='success' toggle={ () => setMessage('') }>{ message }</Alert> }
      { errorMessage && <Alert color='danger' toggle={ () => setErrorMessage('') }>{ errorMessage }</Alert> }

      { auditMode && (
        <Card className='mb-4'><CardBody>
          <div className='d-flex flex-wrap justify-content-between align-items-center'>
            <h3>Progress per SA</h3>
            <Button color='secondary' outline aria-label='Export Progress XLSX' disabled={exportingProgress || progressRangeInvalid}
              onClick={() => exportReport('progress')}>{exportingProgress ? <Spinner size='sm' /> : 'Export XLSX'}</Button>
          </div>
          <p className='text-muted'>Ringkasan dan export mengikuti filter section ini. Progress = upload selesai / (upload selesai + upload pending); REPLACED tidak dihitung selesai. Rentang waktu memakai New Arrival (WIB).</p>
          <ReportFilters filters={progressFilters} onChange={setProgressFilters} assignees={meta.assignees || []} />
          <Button size='sm' color='secondary' outline className='mb-3' onClick={() => setProgressFilters(emptyProgressFilters)}>Reset Filter Progress</Button>
          {progressRangeInvalid && <Alert color='warning'>New Arrival dari tidak boleh melebihi sampai.</Alert>}
          {progressLoading && <div className='mb-2'><Spinner size='sm' /> Memuat progress...</div>}
          <div className='table-responsive'><Table bordered size='sm'>
            <thead className='thead-light'><tr><th>SA</th><th>Category</th><th>Active</th><th>Pending Uploads / Quota</th><th>Completed Uploads</th><th>REPLACED</th><th>Progress</th></tr></thead>
            <tbody>
              { progress.length === 0 && <tr><td colSpan='7' className='text-center'>Belum ada data SA.</td></tr> }
              { progress.map((row) => <tr key={ row.id } role='link' tabIndex='0' className='ig-story-progress-row'
                aria-label={`View assignments for ${getIgStorySaDisplayName(row)}`}
                onClick={() => openSaAssignmentDetail(row.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    openSaAssignmentDetail(row.id)
                  }
                }}>
                <td>{ getIgStorySaDisplayName(row) }<br /><small>{ row.email }</small></td>
                <td>{ row.internal_category || '-' }</td><td>{ row.active ? 'Yes' : 'No' }</td>
                <td>{ row.in_queue } / { row.capacity || '-' }</td><td>{ row.done }</td><td>{ row.replaced }</td>
                <td><div className='progress mb-1'><div className='progress-bar bg-success' role='progressbar'
                  style={{ width: `${row.completion_percent}%` }} aria-valuenow={ row.completion_percent } aria-valuemin='0' aria-valuemax='100' /></div>{ row.completion_percent }%</td>
              </tr>) }
            </tbody>
          </Table></div>
        </CardBody></Card>
      ) }

      <Card className='mb-4'>
        <CardBody>
          <div className='d-flex flex-wrap justify-content-between align-items-center mb-3'>
            <div>
              <h3 className='mb-1'>{ auditMode ? 'Queue Semua SA' : 'Current Queue' }</h3>
              <div className='text-muted'>{ auditMode ? 'Seluruh assignment SA dengan status ASSIGNED atau REPLACED.' : 'Barang aktif yang masih perlu di-upload ke IG Story.' }</div>
            </div>
            <div className='d-flex flex-wrap mt-2 mt-md-0'>
              { viewer.can_audit && (
                <Button color='secondary' outline className='mr-2 mb-2' aria-label='Export Queue XLSX' onClick={ () => exportReport('assignments') } disabled={ exporting || queueRangeInvalid }>
                  { exporting ? <Spinner size='sm' /> : <i className='fas fa-file-excel mr-2' /> }
                  Export XLSX
                </Button>
              ) }
              { !auditMode && (
                <>
                  { selectedQueueIds.length > 0 && (
                    <Button color='secondary' outline className='mr-2 mb-2' disabled={savingBulk}
                      onClick={() => setSelectedQueueIds([])}>Clear Selection</Button>
                  ) }
                  <Button color='success' className='mr-2 mb-2'
                    disabled={ savingBulk || selectedQueueIds.length === 0 }
                    onClick={ saveSelectedQueue }>
                    { savingBulk ? <Spinner size='sm' className='mr-2' /> : <i className='fas fa-check mr-2' /> }
                    Save Selected ({ selectedQueueIds.length })
                  </Button>
                </>
              ) }
            </div>
          </div>

          {queueRangeInvalid && <Alert color='warning'>New Arrival dari tidak boleh melebihi sampai.</Alert>}
          { auditMode && (
            <div className='mb-3' role='group' aria-label='Assignment status'>
              { ['', 'assigned', STATUS.REPLACED].map((value) => (
                <Button key={ value || 'all' } color={ value ? statusColor(value) : 'primary' }
                  outline={ status !== value } className='mr-2 mb-2'
                  onClick={ () => { setStatus(value); setQueuePage(1) } }>
                  { value ? statusLabel(value) : 'ALL' }
                </Button>
              )) }
            </div>
          ) }
          <Row form className='mb-3'>
            <Col lg='4' md='6' className='mb-2'>
              <div className='d-flex'>
                <Input
                  value={ searchInput }
                  onChange={ (event) => setSearchInput(event.target.value) }
                  onKeyDown={ (event) => { if (event.key === 'Enter') setQ(searchInput.trim()) } }
                  placeholder='Search SKU / product name...'
                />
                <Button color='primary' outline className='ml-2' onClick={ () => setQ(searchInput.trim()) }>Search</Button>
              </div>
            </Col>
            <Col lg='2' md='3' className='mb-2'>
              <Input type='select' value={ groupCode } onChange={ (event) => setGroupCode(event.target.value) }>
                { GROUP_OPTIONS.map((value) => <option key={ value || 'all' } value={ value }>{ value || 'All Group' }</option>) }
              </Input>
            </Col>
            { !auditMode && <Col lg='2' md='3' className='mb-2'>
              <Input type='select' aria-label='Product Category' value={ productCategoryId } onChange={ (event) => setProductCategoryId(event.target.value) }>
                <option value=''>ALL Product Category</option>
                { (meta.product_categories || []).map((item) => (
                  <option key={ item.id } value={ item.id }>{ item.name }</option>
                )) }
              </Input>
            </Col> }
            { viewer.can_audit && (
              <>
                <Col lg='2' md='3' className='mb-2'>
                  <Input type='select' value={ category } onChange={ (event) => setCategory(event.target.value) }>
                    { CATEGORY_OPTIONS.map((value) => <option key={ value || 'all' } value={ value }>{ value || 'All Category' }</option>) }
                  </Input>
                </Col>
                <Col lg={ auditMode ? '4' : '2' } md='3' className='mb-2'>
                  <Input type='select' value={ assigneeId } onChange={ (event) => setAssigneeId(event.target.value) }>
                    <option value=''>All SA</option>
                    { (meta.assignees || []).map((assignee) => (
                      <option key={ assignee.id } value={ assignee.id }>
                        { getIgStorySaDisplayName(assignee) } — { assignee.internal_category }
                      </option>
                    )) }
                  </Input>
                </Col>
              </>
            ) }
          </Row>

          { !auditMode && (
            <div className='ig-story-upload-history-filter mb-3'>
              <div className='ig-story-upload-history-filter-description'>
                <Label for='ig-story-upload-history-filter' className='font-weight-bold mb-1'>Upload History</Label>
                <div id='ig-story-upload-history-filter-help' className='text-muted small'>
                  Based on completed uploads (Count). The checkboxes in the first column select products
                  for the next Save Selected; they do not show previous uploads.
                </div>
              </div>
              <Input id='ig-story-upload-history-filter' type='select' value={uploadHistoryFilter}
                className='ig-story-upload-history-filter-select' aria-describedby='ig-story-upload-history-filter-help'
                onChange={(event) => updateMyQueueQuery({ upload_history: event.target.value, selection: '', page: '' })}>
                <option value=''>ALL</option>
                <option value='ever'>Uploaded Before (Count &gt; 0)</option>
                <option value='never'>Never Uploaded (Count = 0)</option>
              </Input>
            </div>
          ) }

          {auditMode && <div className='mb-3'>
            <Row form>
              <Col md='5' className='mb-2'>
                <Label for='queue-arrival-from'>New Arrival dari (WIB)</Label>
                <Input id='queue-arrival-from' aria-label='Queue arrival from WIB' type='datetime-local' value={arrivalFrom} onChange={(event) => setArrivalFrom(event.target.value)} />
              </Col>
              <Col md='5' className='mb-2'>
                <Label for='queue-arrival-to'>New Arrival sampai (WIB)</Label>
                <Input id='queue-arrival-to' aria-label='Queue arrival to WIB' type='datetime-local' value={arrivalTo} onChange={(event) => setArrivalTo(event.target.value)} />
              </Col>
            </Row>
            <Button size='sm' color='secondary' outline onClick={() => {
              setStatus(''); setCountFilter(''); setArrivalFrom(''); setArrivalTo(''); setAssigneeId('')
              setCategory(''); setGroupCode(''); setQ(''); setSearchInput(''); setQueuePage(1)
            }}>Reset Filter Queue</Button>
          </div>}

          <div className='table-responsive'>
            <Table hover bordered size='sm' className='mb-0'>
              <thead className='thead-light'>
                <tr>
                  <th className='text-center' style={{ width: 54 }}>No.</th>
                  { !auditMode && (
                    <th style={{ width: 44 }} className='text-center'>
                      <input
                        type='checkbox'
                        aria-label='Select all assignments on this page'
                        checked={ allCurrentPageSelected }
                        disabled={ savingBulk || loadingQueue }
                        onChange={ toggleCurrentQueuePage }
                      />
                    </th>
                  ) }
                  <th>SKU</th>
                  <th>Product</th>
                  <th>Consignor Name</th>
                  <th>Availability</th>
                  { viewer.can_audit && <th>SA
                    <Input aria-label='Queue SA' type='select' bsSize='sm' value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)}>
                      <option value=''>ALL</option>
                      {(meta.assignees || []).map((sa) => <option key={sa.id} value={sa.id}>{getIgStorySaDisplayName(sa)}</option>)}
                    </Input>
                  </th> }
                  <th>New Arrival</th>
                  <th>Status {auditMode && <Input aria-label='Queue status' type='select' bsSize='sm' value={status} onChange={(event) => setStatus(event.target.value)}><StatusOptions /></Input>}</th>
                  <th title='Total completed upload cycles per product'>Count
                    {auditMode && <Input aria-label='Queue Count' type='select' bsSize='sm' value={countFilter} onChange={(event) => setCountFilter(event.target.value)}><CountOptions /></Input>}
                  </th>
                </tr>
              </thead>
              <tbody>
                { loadingQueue && (
                  <tr><td colSpan={(viewer.can_audit ? 8 : 7) + (auditMode ? 0 : 1) + 1} className='text-center py-5'><Spinner /></td></tr>
                ) }
                { !loadingQueue && queue.length === 0 && (
                  <tr><td colSpan={(viewer.can_audit ? 8 : 7) + (auditMode ? 0 : 1) + 1} className='text-center text-muted py-5'>Tidak ada assignment untuk filter ini.</td></tr>
                ) }
                { !loadingQueue && queue.map((assignment, index) => (
                    <tr key={ assignment.id }
                      tabIndex={0}
                      aria-label={`View details for ${assignment.product?.sku || assignment.id}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => openAssignmentDetail(assignment.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          openAssignmentDetail(assignment.id)
                        }
                      }}>
                      <td className='text-center'>{queueRowStart + index + 1}</td>
                      { !auditMode && (
                        <td className='text-center'
                          onClick={ (event) => event.stopPropagation() }
                          onKeyDown={ (event) => event.stopPropagation() }>
                          <input
                            type='checkbox'
                            aria-label={`Select ${assignment.product?.sku || assignment.id}`}
                            checked={ selectedQueueIdSet.has(assignment.id) }
                            disabled={ savingBulk }
                            onChange={ () => toggleQueueAssignment(assignment.id) }
                            onClick={ (event) => event.stopPropagation() }
                            onKeyDown={ (event) => event.stopPropagation() }
                          />
                        </td>
                      ) }
                      <td><strong>{ assignment.product?.sku || '-' }</strong></td>
                      <td>{ assignment.product?.name || '-' }</td>
                      <td>{ assignment.product?.consignor_name || '-' }</td>
                      <td><AvailabilityBadge product={assignment.product} /></td>
                      { viewer.can_audit && <td>{ getIgStorySaDisplayName(assignment.assignee) }</td> }
                      <td>{ formatDateTime(assignment.arrival_at) }</td>
                      <td><Badge color={ statusColor(assignment.status) }>{ statusLabel(assignment.status) }</Badge></td>
                      <td>{ assignment.upload_count || 0 }</td>
                    </tr>
                )) }
              </tbody>
            </Table>
          </div>

          <QueuePagination meta={meta} loading={loadingQueue} onChange={setQueuePage} />
        </CardBody>
      </Card>

      { auditMode && viewer.can_view_audit_logs && (
        <Card className='mb-4'>
          <CardBody>
            <div className='d-flex justify-content-between align-items-center' style={{ cursor: 'pointer' }} onClick={ toggleAudit }>
              <div>
                <h3 className='mb-1'>Audit Log</h3>
                <div className='text-muted'>Track assignments, replacements, upload confirmations, exports, and team settings.</div>
              </div>
              <i className={ `fas fa-chevron-${auditOpen ? 'up' : 'down'}` } />
            </div>

            <Collapse isOpen={ auditOpen }>
              <Row form className='mt-4 mb-3'>
                <Col md='3' className='mb-2'>
                  <Label>From Date</Label>
                  <Input type='date' value={ fromDate } onChange={ (event) => setFromDate(event.target.value) } />
                </Col>
                <Col md='3' className='mb-2'>
                  <Label>To Date</Label>
                  <Input type='date' value={ toDate } onChange={ (event) => setToDate(event.target.value) } />
                </Col>
                <Col md='3' className='d-flex align-items-end mb-2'>
                  <Button color='primary' outline onClick={ loadAuditLogs }>Apply Audit Filter</Button>
                </Col>
              </Row>

              <div className='table-responsive'>
                <Table hover bordered size='sm'>
                  <thead className='thead-light'>
                    <tr><th>Identity</th><th>Activity</th><th>What Happened</th><th>Actor</th><th>Created At (WIB)</th><th /></tr>
                  </thead>
                  <tbody>
                    { auditLoading && <tr><td colSpan='6' className='text-center py-4'><Spinner /></td></tr> }
                    { !auditLoading && auditLogs.length === 0 && <tr><td colSpan='6' className='text-center text-muted py-4'>No audit activity.</td></tr> }
                    { !auditLoading && auditLogs.map((entry) => <AuditLogRow key={entry.id} entry={entry} />) }
                  </tbody>
                </Table>
              </div>
              <div className='d-flex align-items-center justify-content-between'>
                <small>{ auditMeta.total_count || 0 } audit entries</small>
                <div>
                  <Button size='sm' outline color='secondary' disabled={ auditLoading || auditPage <= 1 }
                    onClick={ () => setAuditPage((page) => page - 1) }>Previous</Button>
                  <small className='mx-2'>Page { auditMeta.current_page || 1 } / { auditMeta.total_pages || 1 }</small>
                  <Button size='sm' outline color='secondary' disabled={ auditLoading || auditPage >= (auditMeta.total_pages || 1) }
                    onClick={ () => setAuditPage((page) => page + 1) }>Next</Button>
                </div>
              </div>
            </Collapse>
          </CardBody>
        </Card>
      ) }

      { viewer.can_manage && (
        <Card className='mb-4'>
          <CardBody>
            <div className='d-flex justify-content-between align-items-center' style={{ cursor: 'pointer' }} onClick={ toggleSetup }>
              <div>
                <h3 className='mb-1'>Team Setup</h3>
                <div className='text-muted'>Konfigurasi SA C/H dan auditor tanpa hardcoded nama atau ID.</div>
              </div>
              <i className={ `fas fa-chevron-${setupOpen ? 'up' : 'down'}` } />
            </div>

            <Collapse isOpen={ setupOpen }>
              <Row form className='mt-4 align-items-end'>
                <Col lg='4' md='6'>
                  <FormGroup>
                    <Label>Administrator</Label>
                    <Input
                      type='select'
                      value={ memberForm.administrator_id }
                      onChange={ (event) => setMemberForm({ ...memberForm, administrator_id: event.target.value }) }
                    >
                      <option value=''>Choose Sales account...</option>
                      { administrators.map((admin) => <option key={ admin.id } value={ admin.id }>{ getIgStorySaDisplayName(admin) } — { admin.role } — { admin.email }</option>) }
                    </Input>
                  </FormGroup>
                </Col>
                <Col lg='2' md='3'>
                  <FormGroup>
                    <Label>Access</Label>
                    <Input
                      type='select'
                      value={ memberForm.member_role }
                      onChange={ (event) => setMemberForm({
                        ...memberForm,
                        member_role: event.target.value,
                        internal_category: event.target.value === 'assignee' ? (memberForm.internal_category || 'C') : ''
                      }) }
                    >
                      <option value='assignee'>Assignee</option>
                      <option value='auditor'>Auditor</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col lg='2' md='3'>
                  <FormGroup>
                    <Label>Category</Label>
                    <Input
                      type='select'
                      disabled={ memberForm.member_role !== 'assignee' }
                      value={ memberForm.internal_category }
                      onChange={ (event) => setMemberForm({ ...memberForm, internal_category: event.target.value }) }
                    >
                      <option value='C'>C — Curated</option>
                      <option value='H'>H — Hermes</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col lg='2' md='3'>
                  <FormGroup>
                    <Label>Capacity</Label>
                    <Input
                      type='number'
                      min='1'
                      max='100'
                      disabled={ memberForm.member_role !== 'assignee' }
                      value={ memberForm.capacity }
                      onChange={ (event) => setMemberForm({ ...memberForm, capacity: Number(event.target.value) || 100 }) }
                    />
                  </FormGroup>
                </Col>
                <Col lg='2' md='3'>
                  <FormGroup>
                    <Button color='primary' block onClick={ createMember } disabled={ setupLoading }>
                      Add Member
                    </Button>
                  </FormGroup>
                </Col>
              </Row>

              <div className='table-responsive'>
                <Table bordered size='sm'>
                  <thead className='thead-light'>
                    <tr><th>Name</th><th>Role</th><th>Category</th><th>Queue</th><th>Capacity</th><th>Active</th><th /></tr>
                  </thead>
                  <tbody>
                    { setupLoading && members.length === 0 && <tr><td colSpan='8' className='text-center py-4'><Spinner /></td></tr> }
                    { members.map((member) => (
                      <tr key={ member.id }>
                        <td>{ getIgStorySaDisplayName(member.administrator) }<br/><small>{ member.administrator?.email }</small></td>
                        <td>{ member.member_role }</td>
                        <td>{ member.internal_category || '-' }</td>
                        <td>{ member.in_queue_count }</td>
                        <td>{ member.member_role === 'assignee' ? member.capacity : '-' }</td>
                        <td>{ member.active ? 'Yes' : 'No' }</td>
                        <td className='text-right'>
                          <Button color='danger' size='sm' outline onClick={ () => deleteMember(member.id) }>Remove</Button>
                        </td>
                      </tr>
                    )) }
                  </tbody>
                </Table>
              </div>

              <Alert color='info' className='mt-3 mb-0'>
                SA kategori C/H dikelola melalui Team Setup. Assignment mengikuti aturan alokasi IG Story yang berlaku.
              </Alert>
            </Collapse>
          </CardBody>
        </Card>
      ) }
    </Container>
  )
}

SalesIgStoryPage.propTypes = propTypes

export default SalesIgStoryPage
